import { CHROMA_BOOST_MAX, CHROMA_BOOST_MIN, findPreset, SCHEME_VARIANTS, type ThemeMode } from "@app/core";
import { Button, RotateCcw, cn } from "@app/ui";
import { useI18n } from "../../../lib/useI18n.js";
import type { I18nKey } from "../../../lib/language.js";
import { boardTargetKey, usePaletteBoardStore } from "../../../stores/index.js";
import { useThemeStore } from "../../../stores/theme.store.js";

const normHex = (hex?: string | null) => hex?.toLowerCase() ?? null;

/** Source seed color(s) + scheme variant + overall vibrancy — the chrome
 *  controls. Clicking a seed color arms it as the palette board's paint target. */
export function SourceColorControls() {
  const { t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const setSource = useThemeStore((s) => s.setSource);
  const revertSource = useThemeStore((s) => s.revertSource);
  const setVariant = useThemeStore((s) => s.setVariant);
  const setChromaBoost = useThemeStore((s) => s.setChromaBoost);
  const armedTarget = usePaletteBoardStore((s) => s.target);
  const arm = usePaletteBoardStore((s) => s.arm);
  const armedKey = armedTarget ? boardTargetKey(armedTarget) : null;
  const preset = findPreset(config.preset);

  const sourceRow = (mode: ThemeMode) => {
    const overridden =
      mode === "dark"
        ? normHex(config.sourceDark) !== normHex(preset.sourceDark ?? null)
        : normHex(config.source) !== normHex(preset.source);
    return (
      <div className="flex items-center gap-1">
        <label
          className={cn("flex items-center gap-2 rounded-md px-1 py-0.5 text-sm", armedKey === `source:${mode}` && "ring-2 ring-primary")}
          onClick={() => arm({ kind: "source", mode })}
        >
          <input
            type="color"
            value={mode === "dark" ? config.sourceDark ?? config.source : config.source}
            onChange={(e) => setSource(e.target.value, mode)}
            onFocus={() => arm({ kind: "source", mode })}
            className="h-8 w-8 cursor-pointer rounded border bg-transparent"
          />
          {t(mode === "dark" ? "theme.sourceDark" : "theme.sourceLight")}
        </label>
        {overridden && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => revertSource(mode)}
            title={t("theme.sourceRevert")}
            aria-label={t("theme.sourceRevert")}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6">
        {sourceRow("light")}
        {sourceRow("dark")}
      </div>

      <div className="flex flex-wrap gap-2">
        {SCHEME_VARIANTS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVariant(v)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm transition-colors",
              config.variant === v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent",
            )}
          >
            {t(`theme.variant.${v}` as I18nKey)}
          </button>
        ))}
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span>{t("theme.vibrancy")}</span>
        <input
          type="range"
          min={CHROMA_BOOST_MIN}
          max={CHROMA_BOOST_MAX}
          step={1}
          value={config.chromaBoost}
          onChange={(e) => setChromaBoost(Number(e.target.value))}
          className="w-full max-w-xs cursor-pointer"
        />
        <span className="text-xs text-muted-foreground">{t("theme.vibrancy.description")}</span>
      </label>
    </div>
  );
}
